import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import { CONFIG } from 'config/environment_variables';
import { IRefreshTokenResponse, JWTPayload } from 'types/token';
import { LOCAL_STORAGE_KEYS } from 'constant/localStorage';
import { ONBOARDING_ENDPOINTS } from 'constant/endpoints/onboarding';
import globalHeaders from 'utils/globalHeaders';
import Decode from 'utils/v1.1/decodeJWT';
import { Logger } from 'utils/logger';
import { logApiRequest } from 'utils/observability/httpLogger';
import { emitErrorLog, emitInfoLog } from 'utils/observability/logger';

const endpointsWithTenantIDExclusion = ['/v1.1/tenant/'];

const isTokenExpired = (tokenData: JWTPayload) => {
  const exp = tokenData?.exp;
  return typeof exp === 'number' && new Date(exp * 1000) < new Date();
};

export const API = axios.create({
  baseURL: `${CONFIG.API_URL}/api/v1`,
});

API.interceptors.request.use(
  async (config) => {
    const isTenantIDIncluded = !endpointsWithTenantIDExclusion.includes(
      config.url ?? '',
    );

    const headers = globalHeaders(isTenantIDIncluded);

    config.headers = { ...config.headers, ...headers } as AxiosRequestHeaders;
    logApiRequest(config);
    return config;
  },
  (error) => Promise.reject(error),
);

/** Use for engineer/support endpoints that must NOT send tenant-id header. */
export const SUPPORT_API = axios.create({
  baseURL: `${CONFIG.API_URL}/api/v1`,
});

SUPPORT_API.interceptors.request.use(
  async (config) => {
    const headers = globalHeaders(false); // never include tenant-id
    config.headers = { ...config.headers, ...headers } as AxiosRequestHeaders;
    logApiRequest(config);
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Attaches a token-refresh response interceptor to an axios instance.
 * Both API and SUPPORT_API use the same refresh logic; only the instance
 * used to retry the original request differs, and API has an extra
 * best-effort onboarding patch on hard-logout.
 */
function addRefreshInterceptor(
  instance: AxiosInstance,
  onRefreshInvalid?: () => Promise<void>,
): void {
  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      if (err.response?.data?.error?.is_refresh_invalid) {
        if (onRefreshInvalid) {
          await onRefreshInvalid();
        }
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH);
        emitInfoLog({
          body: {
            message: '[AUTO LOGOUT]: Token refresh invalid',
          },
          attrs: {
            source: 'AxiosInterceptor',
          },
        });
        window.location.assign('/login');
      } else if (
        err.response?.data?.error?.is_access_invalid === 'True' &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        try {
          if (isTokenExpired(Decode())) {
            const response: { data: IRefreshTokenResponse } = await API.post(
              '/v1.1/token/refresh/',
              {
                refresh: localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH),
                'tenant-id': localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT),
              },
            );
            const { access } = response.data;
            localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN, access);
            originalConfig.headers.Authorization = `Bearer ${access}`;
          }
          return instance(originalConfig);
        } catch (_error) {
          emitErrorLog({
            body: {
              message: '[REFRESH TOKEN]: Token refresh failed',
            },
            attrs: {
              source: 'AxiosInterceptor',
            },
          });
          return Promise.reject(_error);
        }
      }
      return Promise.reject(err);
    },
  );
}

addRefreshInterceptor(SUPPORT_API);

addRefreshInterceptor(API, async () => {
  try {
    await API.patch(ONBOARDING_ENDPOINTS.TENANT_ONBOARDING_SKIPPED, {
      is_onboarding_skipped: false,
    });
  } catch (error) {
    Logger.error({
      message: 'Onboarding skipped patch failed on logout:',
      data: error,
    });
  }
});
